import type { IAnnualReportAccordionsData } from '@modeso/aumico-lib-notes-fe';
import { cloneDeep } from 'lodash-es';
import isEqual from 'lodash.isequal';

/** Set on a section by the editor (open / completeness marker); never something the user typed. */
const SECTION_VIEW_KEYS = ['isExpanded', 'filled'];

/**
 * Copied into every component from the report settings each time the notes open (currency and the
 * balance / income dates, in display form) — the user cannot edit them in the notes.
 */
const COMPONENT_SETTINGS_KEYS = [
    'currency',
    'currentDate',
    'previousDate',
    'currentYearDate',
    'previousYearDate',
    'currentBalanceYearDate',
    'previousBalanceYearDate',
    'currentIncomeYearDate',
    'previousIncomeYearDate',
];

interface IComparableSection {
    data?: { data?: Record<string, unknown> }[];
}

/**
 * REV-22548: whether leaving the notes should ask to save. The editor's copy of the notes differs from
 * the saved document in shape only — view markers, settings copied in on open, the version stamp,
 * key order — so those are left out and only what the user can change is compared.
 */
export class NotesUnsavedChangesHelper {
    static isChanged(saved: IAnnualReportAccordionsData | null | undefined, current: IAnnualReportAccordionsData | null | undefined): boolean {
        return !isEqual(NotesUnsavedChangesHelper.comparable(saved), NotesUnsavedChangesHelper.comparable(current));
    }

    private static comparable(notes: IAnnualReportAccordionsData | null | undefined): unknown {
        if (notes == null) {
            return null;
        }
        // A JSON round trip drops `undefined` values, so a missing key and an undefined one compare equal.
        const copy = JSON.parse(JSON.stringify(cloneDeep(notes)));
        delete copy.version;
        (copy.accordions ?? []).forEach((section: IComparableSection) => {
            SECTION_VIEW_KEYS.forEach((key) => delete (section as Record<string, unknown>)[key]);
            (Array.isArray(section?.data) ? section.data : []).forEach((component) => {
                const data = component?.data;
                if (data != null && typeof data === 'object') {
                    COMPONENT_SETTINGS_KEYS.forEach((key) => delete data[key]);
                }
            });
        });
        return copy;
    }
}
